import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CartographySelectorModal, getSourceGroupsFiltered, POIBrandsContent } from 'sonata-platform-components';
import { ButtonUI, Checkbox, showNotification } from 'sonata-react-components';

import { usePOIImporter } from '@/components/common/POIImporter/POIImporter.context';
import { postAddVenues } from '@/services/venue.service';
import { ATTRIBUTION_RADIUS_IN_METERS } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { findMatchingTemporaryVenue, restoreVenueAssociation } from '../../../MultiFlightMap.helpers';
import { AttributionRadiusPrompt } from '../AttributionRadiusPrompt/AttributionRadiusPrompt';
import { MyVenues } from './MyVenues/MyVenues';

import type { AdminBoundariesChangeItem, AdminBoundariesItemInput } from 'sonata-platform-components';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import './POIInventory.scss';

type State = {
  source: string | null;
  country: string | null;
  brands: string[];
  categories: string[];
  ids: string[];
  isLoading: boolean;
  isAddActivationAreasEnabled: boolean;
  showRadiusPrompt: boolean;
  showAdminBoundariesModal: boolean;
  cartographyTagFilters: Record<string, string[]>;
  selectedItemsData?: Record<string, any>[];
};

export function POIInventory() {
  const { formatMessage } = useIntl();
  const [
    {
      brands,
      categories,
      cartographyTagFilters,
      country,
      ids,
      isAddActivationAreasEnabled,
      isLoading,
      selectedItemsData,
      showAdminBoundariesModal,
      showRadiusPrompt,
      source
    },
    setState
  ] = useState<State>({
    brands: [],
    cartographyTagFilters: {},
    categories: [],
    country: null,
    ids: [],
    isAddActivationAreasEnabled: false,
    isLoading: false,
    selectedItemsData: [],
    showAdminBoundariesModal: false,
    showRadiusPrompt: false,
    source: null
  });
  const {
    countries,
    entityId,
    entityType,
    loadingSourcesList,
    sources,
    temporaryVenues,
    updateVenue,
    user,
    userRoles,
    venuesSourceData,
    onAddBatchVenues,
    addLocationCircle
  } = useMultiFlightMap();
  const { attributionMode, setShowModal } = usePOIImporter();

  const isProximityPointsMode = attributionMode === 'proximityPoints';

  const handleStateChange = (poiData: {
    dataSourceType: string;
    selectedCountry: string;
    selectedItems: Set<{ brand: string; category: string; id: string }>;
  }) => {
    const selectedItemsData = Array.from(poiData.selectedItems).reduce(
      (acc, entry) => {
        return {
          brands: [...acc.brands, entry.brand],
          categories: [...acc.categories, entry.category],
          ids: [...acc.ids, entry.id]
        };
      },
      { brands: [], categories: [], ids: [] } as { brands: string[]; categories: string[]; ids: string[] }
    );
    setState(prev => ({
      ...prev,
      brands: selectedItemsData.brands,
      categories: selectedItemsData.categories,
      country: poiData.selectedCountry,
      ids: selectedItemsData.ids,
      selectedItemsData: Array.from(poiData.selectedItems),
      source: poiData.dataSourceType
    }));
  };

  const handleSave = async (radiusInMeters?: number) => {
    const attributionOptions = {
      attribution: true,
      attributionMode: isProximityPointsMode ? ('footfall' as const) : ('driveToStore' as const),
      ...(isProximityPointsMode && radiusInMeters && { attributionRadiusInMeters: radiusInMeters })
    };

    // "My Venues" (Proximity Points source): items are the user's own previously
    // added points, which may not exist on Google Maps. They carry `latlng`
    // directly (no `coordinates`/`geoDataId` like POI Brands items), and are
    // filtered by selected `ids` instead of brand/category (no taxonomy applies).
    const addMyVenuesPoints = async (): Promise<{ addedCount: number; skippedCount: number } | null> => {
      const venues = (selectedItemsData as Record<string, any>[])
        .filter(item => item.latlng?.lat !== undefined && item.latlng?.lng !== undefined)
        .map(
          (item): Partial<Venue> => ({
            address: item.address,
            country: item.country ?? country,
            deleted: false,
            groups: [],
            latlng: item.latlng,
            name: item.name,
            sourceType: 'myVenues',
            type: item.type ?? 'retail',
            user: user._id
          })
        );
      // No stable client-side identifier survives the round trip for "My Venues"
      // points (they don't carry `geoDataId`), so duplicates already added to this
      // entity are detected heuristically by name + coordinates.
      const existingVenueKeys = new Set(
        temporaryVenues.filter(v => !v.deleted).map(v => `${v.name}|${v.latlng?.lat}|${v.latlng?.lng}`)
      );
      const venuesToAdd = venues.filter(v => !existingVenueKeys.has(`${v.name}|${v.latlng?.lat}|${v.latlng?.lng}`));
      const skippedCount = venues.length - venuesToAdd.length;
      if (venuesToAdd.length === 0) {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'All selected venues are already in your campaign.',
            id: 'poiImporter.notification.add.allDuplicates'
          })
        );
        setShowModal(false);
        return null;
      }
      const persisted = await postAddVenues(venuesToAdd as Venue[], {});
      await onAddBatchVenues(persisted as Partial<Venue<string>>[], attributionOptions);
      if (isAddActivationAreasEnabled && radiusInMeters) {
        await Promise.all(
          persisted
            .filter(venue => venue.latlng?.lat && venue.latlng?.lng)
            .map(venue => addLocationCircle(venue.latlng.lat, venue.latlng.lng, radiusInMeters / 1000))
        );
      }
      return { addedCount: persisted.length, skippedCount };
    };

    const addVenues = async (): Promise<{ addedCount: number; skippedCount: number } | null> => {
      if (source === 'myVenues') {
        return addMyVenuesPoints();
      }
      const tokenGroup = {};
      const poiBrandsItems = await getSourceGroupsFiltered(
        source,
        country,
        {
          brands,
          categories
        },
        { cartographyFilter: cartographyTagFilters },
        tokenGroup
      );
      const venues = poiBrandsItems.map((item: Record<string, any>) => ({
        address: item.address,
        country: country,
        deleted: false,
        geoDataId: item.id,
        geoDataSourceId: source,
        groups: [],
        latlng: {
          lat: item.coordinates.latitude,
          lng: item.coordinates.longitude
        },
        name: item.name,
        sourceType: 'poiBrands',
        type: 'retail',
        user: user._id
      }));
      const venuesToRestore: Partial<Venue>[] = [];
      const venuesToCreate: typeof venues = [];
      let alreadyAssociatedCount = 0;
      venues.forEach((candidate: Partial<Venue>) => {
        const match = findMatchingTemporaryVenue(candidate, temporaryVenues);
        if (!match) {
          venuesToCreate.push(candidate);
        } else if (match.deleted) {
          venuesToRestore.push(restoreVenueAssociation(match, entityId, entityType));
        } else {
          alreadyAssociatedCount += 1;
        }
      });
      if (venuesToCreate.length === 0 && venuesToRestore.length === 0) {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'All selected venues are already in your campaign.',
            id: 'poiImporter.notification.add.allDuplicates'
          })
        );
        setShowModal(false);
        return null;
      }
      if (venuesToRestore.length > 0) {
        updateVenue(venuesToRestore);
      }
      let persisted: Venue<string>[] = [];
      if (venuesToCreate.length > 0) {
        persisted = (await postAddVenues(venuesToCreate as Venue[], {})) as Venue<string>[];
        await onAddBatchVenues(persisted as Partial<Venue<string>>[], attributionOptions);
        if (isAddActivationAreasEnabled && radiusInMeters) {
          await Promise.all(
            persisted
              .filter(venue => venue.latlng?.lat && venue.latlng?.lng)
              .map(venue => addLocationCircle(venue.latlng.lat, venue.latlng.lng, radiusInMeters / 1000))
          );
        }
      }
      return { addedCount: persisted.length + venuesToRestore.length, skippedCount: alreadyAssociatedCount };
    };
    if ((isAddActivationAreasEnabled || isProximityPointsMode) && !showRadiusPrompt) {
      setState(prev => ({ ...prev, showRadiusPrompt: true }));
      return;
    }
    try {
      setState(prev => ({ ...prev, isLoading: true, showRadiusPrompt: false }));
      const result = await addVenues();
      if (result === null) {
        return;
      }
      setShowModal(false);
      if (result.skippedCount > 0) {
        showNotification(
          'success',
          formatMessage(
            {
              defaultMessage: '{added} POIs added. {skipped} venue(s) skipped — already in your campaign.',
              id: 'poiImporter.notification.add.successWithSkipped'
            },
            { added: result.addedCount, skipped: result.skippedCount }
          )
        );
      } else {
        showNotification(
          'success',
          formatMessage({ defaultMessage: 'POIs added successfully', id: 'poiImporter.notification.add.success' })
        );
      }
    } catch (_) {
      showNotification(
        'error',
        formatMessage({ defaultMessage: "Couldn't add POIs, try again", id: 'poiImporter.notification.add.error' })
      );
    } finally {
      setState(prev => ({ ...prev, isAttributionEnabled: false, isLoading: false, showRadiusPrompt: false }));
    }
  };

  const handleCartographySave = (zoneList: AdminBoundariesChangeItem[]) => {
    const tagFilters = zoneList.reduce(
      (acc, item) => ({
        ...acc,
        [item.shape]: item.list.map((e: AdminBoundariesItemInput) => e.name)
      }),
      {} as Record<string, string[]>
    );
    setState(prev => ({
      ...prev,
      cartographyTagFilters: tagFilters,
      showAdminBoundariesModal: false
    }));
  };

  const handleMyVenuesSelectionChange = (venues: Venue[]) => {
    setState(prev => ({
      ...prev,
      country: venues[0]?.country ?? prev.country,
      ids: venues.map(v => v._id!),
      selectedItemsData: venues,
      source: 'myVenues'
    }));
  };

  const poiBrandSources = useMemo(
    () => sources.allSources.filter(source => source.type === 'poi' && source.source.includes('poibrand')),
    [sources.allSources]
  );
  const hasNoSelection = isProximityPointsMode ? ids.length === 0 : brands.length === 0 && categories.length === 0;

  const handleCartographyReset = () => {
    setState(prev => ({ ...prev, cartographyTagFilters: {} }));
  };

  return (
    <div className="poiInventory">
      <div className="poiInventory__brands">
        {isProximityPointsMode ? (
          <MyVenues onSelectionChange={handleMyVenuesSelectionChange} theme="light" user={user} />
        ) : (
          <POIBrandsContent
            campaign={{ _id: entityId }}
            countries={countries.map(c => c.toLocaleUpperCase())}
            filterAddedShops={temporaryVenues.filter(v => !v.deleted && v._id).map(v => v._id!)}
            includeMyVenuesSource={isProximityPointsMode}
            isLoading={isLoading || loadingSourcesList}
            isModalOpened={true}
            onAdminBoundariesClick={() => setState(prev => ({ ...prev, showAdminBoundariesModal: true }))}
            onAdminBoundariesReset={handleCartographyReset}
            onStateChange={handleStateChange}
            origin={'venues'}
            originFilters={{
              layerIsochroneFilter: {},
              poiFilters: {},
              tagFilters: cartographyTagFilters
            }}
            shouldFeatureAddFromCampaign={false}
            shouldHideSourceTypeSelector
            sources={isProximityPointsMode ? [] : poiBrandSources}
            theme="light"
            user={user}
            userRoles={userRoles}
            venuesSourceData={venuesSourceData}
            viewport={{}}
          />
        )}
      </div>
      <div className="poiInventory__actions">
        <Checkbox
          active={isAddActivationAreasEnabled}
          disabled={hasNoSelection || isLoading}
          id="poiInventory-activationAreas"
          labelText={formatMessage({
            defaultMessage: 'Add & set as Activation Areas',
            id: `poiImporter.checkbox.activationAreas`
          })}
          onClickAction={() =>
            setState(prevState => ({
              ...prevState,
              isAddActivationAreasEnabled: !prevState.isAddActivationAreasEnabled
            }))
          }
        />
        <ButtonUI
          disabled={hasNoSelection || isLoading}
          icon={isProximityPointsMode ? 'fa-solid fa-thumbtack' : 'fa-kit fa-sharp-regular-location-dot-circle-plus'}
          iconPosition="left"
          id="poiInventory-addPOIs"
          onClick={handleSave}
          shape="squareRounded"
          text={
            isProximityPointsMode
              ? formatMessage({
                  defaultMessage: 'Add Coordinates',
                  id: 'poiImporter.button.addPoints'
                })
              : formatMessage({
                  defaultMessage: 'Add POIs',
                  id: 'poiImporter.button.addPOIs'
                })
          }
          type="primary"
        />
      </div>
      {(isAddActivationAreasEnabled || isProximityPointsMode) && showRadiusPrompt ? (
        <AttributionRadiusPrompt
          defaultRadiusInMeters={isProximityPointsMode ? ATTRIBUTION_RADIUS_IN_METERS : undefined}
          onCancel={() => setState(prev => ({ ...prev, isAttributionEnabled: false, showRadiusPrompt: false }))}
          onConfirm={handleSave}
          title={
            isProximityPointsMode
              ? formatMessage({
                  defaultMessage: 'Confirm the radius for your Proximity Coordinates',
                  id: 'poiImporter.prompt.proximityRadius.title'
                })
              : undefined
          }
        />
      ) : null}
      {showAdminBoundariesModal && (
        <CartographySelectorModal
          id="poiInventory-cartographySelectorModal"
          initialCountry={countries[0]?.toUpperCase()}
          initialItems={Object.entries(cartographyTagFilters).flatMap(([shape, ids]) =>
            ids.map(
              (name): AdminBoundariesItemInput => ({
                country: countries[0]?.toUpperCase() ?? '',
                description: name,
                name,
                shape
              })
            )
          )}
          isModalOpened={showAdminBoundariesModal}
          onClose={() => setState(prev => ({ ...prev, showAdminBoundariesModal: false }))}
          onSave={handleCartographySave}
          oneBoundaryType
          sonataLICountries
          theme="light"
          user={user}
          userRoles={userRoles}
        />
      )}
    </div>
  );
}
